import { Label } from '@consigli/facade';
import { RfpRegion, RfpRegionLabels } from '@consigli/types';
import { FC, useState } from 'react';
import { FieldValues, useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export const SelectRfpRegion: FC = () => {
  const { t } = useTranslation();
  const { control } = useFormContext<FieldValues>();
  const {
    field: rfpRegionField,
    fieldState: { error },
  } = useController({
    name: 'rfpRegion',
    control,
    rules: { required: t('rfp-region.required') },
  });

  const [search, setSearch] = useState('');

  const options = Object.entries(RfpRegionLabels)
    .map(([value, label]) => ({ value: value as RfpRegion, label }))
    .filter(({ label }) => label.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="my-4">
      <Label text={t('rfp-region.heading')} />
      <p className="mb-3 text-xs text-gray-500">{t('rfp-region.helper')}</p>

      <div className="rounded border border-day-secondary bg-white shadow-sm">
        <div className="border-b border-day-secondary px-3 py-2">
          <input
            type="text"
            placeholder="Search Region"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="w-full bg-transparent text-sm text-day-neutral-dark placeholder-day-neutral-subtle outline-none"
          />
        </div>

        <div className="grid grid-cols-2 gap-x-4 gap-y-3 p-3">
          {options.map(({ value, label }) => {
            const isSelected = rfpRegionField.value === value;
            return (
              <label
                key={value}
                htmlFor={`rfp-region-${value}`}
                className="flex cursor-pointer items-center gap-2"
              >
                <input
                  type="checkbox"
                  id={`rfp-region-${value}`}
                  checked={isSelected}
                  onChange={() => rfpRegionField.onChange(value)}
                  className={[
                    'appearance-none h-4 w-4 shrink-0 rounded border outline-none',
                    'border-day-secondary bg-day-light-4',
                    'focus:ring-1 focus:ring-day-primary cursor-pointer',
                    'relative',
                    isSelected
                      ? "bg-night-dark-1 before:content-['✓'] before:absolute before:text-xs before:font-bold before:text-white before:top-[-1px] before:left-[2px]"
                      : '',
                  ].join(' ')}
                />
                <span className="text-sm font-semibold text-day-neutral-dark">{label}</span>
              </label>
            );
          })}

          {options.length === 0 && (
            <p className="col-span-2 py-2 text-center text-sm text-day-neutral-subtle">
              No regions match your search
            </p>
          )}
        </div>
      </div>

      {error && <p className="mt-1 text-sm text-red-500">{error.message}</p>}
    </div>
  );
};
